"use strict";
/**
 * Naming rules of the public hosts below the managed apex, shared by the
 * server (which enforces them) and the clients that show a rule before the
 * round trip or build a link. Apex-independent: every function takes the
 * host it works on.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BRANDED_LABEL_PATTERN = exports.APP_BASE_SLUG_PATTERN = exports.MAX_BRANDED_LABEL_LENGTH = exports.MIN_BRANDED_LABEL_LENGTH = exports.APP_BASE_SLUG_MAX_LENGTH = exports.APP_BASE_SLUG_MIN_LENGTH = exports.CONTAINER_HOST_SEPARATOR = exports.MAX_DNS_LABEL_LENGTH = void 0;
exports.isRegistrableBrandedLabelFormat = isRegistrableBrandedLabelFormat;
exports.buildContainerHost = buildContainerHost;
exports.buildContainerOrigin = buildContainerOrigin;
exports.buildContainerPublicUrl = buildContainerPublicUrl;
/** DNS limit of one label; a container host is a single label below the apex. */
exports.MAX_DNS_LABEL_LENGTH = 63;
/**
 * Separates the App Container slug from the workspace label in a container
 * host (`<slug>--<label>.<apex>`). Neither part may contain it, so the host
 * reads back without a lookup.
 */
exports.CONTAINER_HOST_SEPARATOR = '--';
/**
 * Length bounds of an App Container slug. A two-character slug would put the
 * separator at label positions 3-4, which DNS reserves for `xn--` names.
 */
exports.APP_BASE_SLUG_MIN_LENGTH = 3;
exports.APP_BASE_SLUG_MAX_LENGTH = 30;
/**
 * Length bounds of a newly registered branded label. The longest App
 * Container slug, the separator and the longest label fill exactly one DNS
 * label, so every container of every workspace has a host.
 */
exports.MIN_BRANDED_LABEL_LENGTH = 3;
exports.MAX_BRANDED_LABEL_LENGTH = exports.MAX_DNS_LABEL_LENGTH -
    exports.CONTAINER_HOST_SEPARATOR.length -
    exports.APP_BASE_SLUG_MAX_LENGTH;
/**
 * App Container slug: single internal hyphens only, so the separator never
 * occurs inside it.
 */
exports.APP_BASE_SLUG_PATTERN = new RegExp(`^(?=.{${exports.APP_BASE_SLUG_MIN_LENGTH},${exports.APP_BASE_SLUG_MAX_LENGTH}}$)[a-z0-9]+(?:-[a-z0-9]+)*$`);
/**
 * Shape of a branded subdomain label (lowercase chars/digits with optional
 * single internal hyphens, no leading/trailing hyphen, at most one DNS
 * label). A double hyphen is reserved as `CONTAINER_HOST_SEPARATOR`. New
 * labels are additionally bounded by `MAX_BRANDED_LABEL_LENGTH`, see
 * `isRegistrableBrandedLabelFormat`.
 */
exports.BRANDED_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9]|-(?!-)){1,61}[a-z0-9]$/;
/** Format a branded label must have to be registered or renamed to. */
function isRegistrableBrandedLabelFormat(label) {
    return (exports.BRANDED_LABEL_PATTERN.test(label) &&
        label.length <= exports.MAX_BRANDED_LABEL_LENGTH);
}
/**
 * Builds the host of one App Container below a workspace host
 * (`<label>.<apex>`, with or without port). Returns null when the slug
 * cannot be carried or the combined label exceeds the DNS label limit.
 */
function buildContainerHost(appBaseSlug, workspaceHost) {
    const dot = workspaceHost.indexOf('.');
    if (dot <= 0 || !exports.APP_BASE_SLUG_PATTERN.test(appBaseSlug))
        return null;
    const label = `${appBaseSlug}${exports.CONTAINER_HOST_SEPARATOR}${workspaceHost.slice(0, dot)}`;
    return label.length > exports.MAX_DNS_LABEL_LENGTH
        ? null
        : `${label}${workspaceHost.slice(dot)}`;
}
/**
 * Origin of an App Container on a workspace origin (`scheme://host[:port]`
 * of a default or branded workspace host). Null where the container name
 * does not fit into the hostname.
 */
function buildContainerOrigin(appBaseSlug, workspaceOrigin) {
    const at = workspaceOrigin.indexOf('://');
    if (at <= 0)
        return null;
    const host = buildContainerHost(appBaseSlug, workspaceOrigin.slice(at + 3));
    return host ? `${workspaceOrigin.slice(0, at + 3)}${host}` : null;
}
/**
 * Public address of an App Container of the workspace at `workspaceOrigin`:
 * its own origin, else `/p/<slug>` below the workspace.
 */
function buildContainerPublicUrl(appBaseSlug, workspaceOrigin) {
    return (buildContainerOrigin(appBaseSlug, workspaceOrigin) ??
        `${workspaceOrigin}/p/${appBaseSlug}`);
}
